import { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ResultForm } from "@/components/competition/ResultForm";
import { Pill, Player, run, stageLabel } from "@/components/competition/shared";

function Score({ m, users }) {
  const r = m.result;
  if (m.status === "bye") return <p className="font-mono text-xs text-slate-400">{m.players.length ? "Bye" : "Empty slot"}</p>;
  if (!r) return null;
  if ("winner_team" in r) return <p className="font-mono text-sm text-amber-100" data-testid="match-score">{r.winner_team === null || r.winner_team === undefined ? "Draw" : `Team ${"AB"[r.winner_team]} won`}{r.forfeit ? " · forfeit" : ""}</p>;
  if (r.placements) return <ol className="font-mono text-xs text-slate-300">{r.placements.map((p, i) => <li key={p}>#{i + 1} @{users[p]?.username}</li>)}</ol>;
  if (!r.games) return null;
  return <p className="font-mono text-sm text-amber-100" data-testid="match-score">{m.players.map((p) => r.games[p]).join(" – ")}{r.draws ? ` (${r.draws} drawn)` : ""}{r.forfeit ? " · forfeit" : ""}</p>;
}

function Actions({ m, users, canManage, refresh }) {
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const me = user?.user_id;
  const mine = me && m.players.includes(me);
  const done = () => { setMode(null); refresh(); };
  const post = (path, body, msg) => run(() => api.post(`/matches/${m.match_id}/${path}`, body), msg, done);
  const forms = {
    report: ["Submit result", null, (result) => post("report", { result }, "Result reported: waiting for confirmation")],
    resolve: ["Settle result", "Organizer note", (result, note) => post("resolve", { result, note }, "Result settled")],
    correct: ["Save correction", "Reason for the correction", (result, reason) => post("correct", { result, reason }, "Result corrected")],
  };
  if (mode === "dispute") return <DisputeForm onSubmit={(reason) => post("dispute", { reason }, "Dispute sent to the organizers")} onCancel={() => setMode(null)} />;
  if (mode) return <ResultForm m={m} users={users} label={forms[mode][0]} textLabel={forms[mode][1]} onSubmit={forms[mode][2]} onCancel={() => setMode(null)} />;
  const btn = "rounded border px-2 py-1 text-xs transition-colors";
  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {mine && (m.status === "scheduled" || (m.status === "reported" && m.reported_by === me)) && <button className={`${btn} border-amber-600 text-amber-200 hover:bg-amber-900/30`} onClick={() => setMode("report")} data-testid="match-report-button">{m.status === "reported" ? "Edit report" : "Report result"}</button>}
      {mine && m.status === "reported" && !m.confirmations.includes(me) && <>
        <button className={`${btn} border-emerald-600 text-emerald-200 hover:bg-emerald-900/30`} onClick={() => post("confirm", undefined, "Result confirmed")} data-testid="match-confirm-button">Confirm</button>
        <button className={`${btn} border-red-700 text-red-300 hover:bg-red-900/30`} onClick={() => setMode("dispute")} data-testid="match-dispute-button">Dispute</button></>}
      {canManage && ["scheduled", "reported", "disputed"].includes(m.status) && <button className={`${btn} border-aether-500 text-aether-300 hover:bg-aether-900/30`} onClick={() => setMode("resolve")} data-testid="match-resolve-button">Settle</button>}
      {canManage && m.status === "confirmed" && <button className={`${btn} border-[#2A3447] text-slate-300 hover:border-amber-600`} onClick={() => setMode("correct")} data-testid="match-correct-button">Correct</button>}
    </div>
  );
}

function DisputeForm({ onSubmit, onCancel }) {
  const [reason, setReason] = useState("");
  return (
    <form className="mt-2 flex gap-2" onSubmit={(e) => { e.preventDefault(); onSubmit(reason); }}>
      <input className="field py-1 text-sm" placeholder="What's wrong with this result?" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dispute-reason-input" />
      <button className="btn-gold px-3 py-1 text-xs" data-testid="dispute-submit">Send</button>
      <button type="button" className="btn-outline px-3 py-1 text-xs" onClick={onCancel}>Cancel</button>
    </form>
  );
}

export function MatchCard({ m, users, canManage, refresh, compact }) {
  const g = m.result?.games;
  const [a, b] = m.players;
  const w = m.result?.placements?.[0] || (g ? (g[a] > g[b] ? a : g[b] > g[a] ? b : null) : m.status === "bye" ? a : null);
  return (
    <article className={`panel p-3 ${m.status === "disputed" ? "border-red-800/70" : ""}`} data-testid={`match-${m.match_id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{stageLabel(m)}{m.table ? ` · table ${m.table}` : ""}</span>
        <Pill value={m.status} testid="match-status" />
      </div>
      <div className={`mt-2 ${compact ? "space-y-0.5" : "space-y-1"} text-sm`}>
        {(m.players.length ? m.players : [null, null]).map((p, i) => <div key={p || i} className={p && p === w ? "font-semibold text-amber-200" : ""}><Player user={p && users[p]} /></div>)}
      </div>
      <Score m={m} users={users} />
      {m.status === "disputed" && m.dispute && <p className="mt-1 text-xs text-red-300" data-testid="match-dispute-reason">Disputed: {m.dispute.reason}</p>}
      <Actions m={m} users={users} canManage={canManage} refresh={refresh} />
    </article>
  );
}
